import { FormEvent, useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { TableCard } from "@/components/blocks/TableCard"
import { Avatar } from "@/components/catalyst/avatar"
import { Badge } from "@/components/catalyst/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Pagination } from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"

type UserRow = {
  id: number
  email: string
  name: string | null
  admin: boolean
  instructor: boolean
  disabled: boolean
  created_at: string
}

type Props = {
  users: UserRow[]
  pagination: { page: number; pages: number; total: number; per_page: number }
  q: string
  role: "all" | "admin" | "instructor" | "user"
}

const ROLE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "admin", label: "Admin" },
  { value: "instructor", label: "Instructor" },
  { value: "user", label: "User" },
] as const

function query(q: string, role: string, page?: number) {
  const params = new URLSearchParams()
  if (q) params.set("q", q)
  if (role !== "all") params.set("role", role)
  if (page && page > 1) params.set("page", String(page))
  const string = params.toString()
  return string ? `/admin/users?${string}` : "/admin/users"
}

export default function AdminUsersIndex({ users, pagination, q, role }: Props) {
  const [term, setTerm] = useState(q)
  const filtered = q !== "" || role !== "all"

  const visit = (nextTerm: string, nextRole: string) =>
    router.get(query(nextTerm.trim(), nextRole), {}, { preserveState: true, replace: true })

  const search = (e: FormEvent) => {
    e.preventDefault()
    visit(term, role)
  }

  return (
    <>
      <Head title="Users">
        <meta name="description" content="Manage users in the admin area." />
        <meta property="og:title" content="Users" />
        <meta property="og:description" content="Manage users in the admin area." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <h1>Users</h1>
          <p className="mt-1">
            {pagination.total} {pagination.total === 1 ? "user" : "users"}
            {filtered ? " match these filters" : " in this app"}.
          </p>
        </div>

        <form onSubmit={search} role="search" className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-2">
            <label htmlFor="user-search">Search</label>
            <Input
              id="user-search"
              type="search"
              placeholder="Name or email"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="space-y-2 sm:w-44">
            <label htmlFor="user-role">Role</label>
            <Select id="user-role" value={role} onChange={(e) => visit(term, e.target.value)}>
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit">
            <Search className="h-4 w-4" /> Search
          </Button>
          {filtered && (
            <Button asChild variant="ghost">
              <Link href="/admin/users">Clear</Link>
            </Button>
          )}
        </form>

        {users.length === 0 ? (
          <div className="callout mt-6">
            <p>No users match these filters.</p>
          </div>
        ) : (
          <div className="mt-6">
            <TableCard>
              <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
                <TableHead>
                  <TableRow>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Role</TableHeader>
                    <TableHeader className="hidden sm:table-cell">Joined</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Link
                          href={`/admin/users/${user.id}`}
                          className="flex items-center gap-3 no-underline after:absolute after:inset-0 hover:text-ink-display"
                        >
                          <Avatar
                            initials={(user.name ?? user.email).charAt(0).toUpperCase()}
                            className="size-9 bg-surface text-ink-body"
                          />
                          <span className="min-w-0">
                            <span className="block font-medium text-ink-display">{user.name ?? user.email}</span>
                            {user.name && <span className="block text-xs text-ink-muted">{user.email}</span>}
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex flex-wrap items-center gap-2">
                          {user.admin && <Badge color="accent">Admin</Badge>}
                          {user.instructor && <Badge color="zinc">Instructor</Badge>}
                          {user.disabled && <Badge color="amber">Disabled</Badge>}
                          {!user.admin && !user.instructor && !user.disabled && <span className="text-ink-muted">User</span>}
                        </span>
                      </TableCell>
                      <TableCell className="hidden text-ink-muted sm:table-cell">{formatDate(user.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableCard>
          </div>
        )}

        <Pagination
          className="mt-4"
          page={pagination.page}
          pages={pagination.pages}
          total={pagination.total}
          perPage={pagination.per_page}
          hrefFor={(page) => query(q, role, page)}
        />
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
